import React, { useEffect, useState } from 'react';
import { fetchApi } from '../../services/api';
import LoadingPanel from '../../components/LoadingPanel';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { JsonResponse } from 'TypeGen/json-response';
import { formatPhone } from '../../utils/utils';

type Props = {
  OrderID: number;
  CloseDialog: () => void;
};

type TimelineEntry = {
  EventDescription: string;
  OccurredDateTime: string;
  TimeAgo: string;
  Detail: string;
};

type DispatchConfirmation = {
  Exists: boolean;
  Status: number;
  CallAttemptCount: number;
  Timeline: TimelineEntry[];
  VehicleNumber: string | null;
  DriverNumber: string | null;
  DriverPhone: string | null;
};

// Mirrors DispatchConfirmationStatus in LoadOne.Types.
const CONFIRMED_IN_APP = 1;
const CONFIRMED_BY_CALL = 2;
const CALL_IN_PROGRESS = 3;
const MANUALLY_RESOLVED = 6;
const CALLBACK_REQUESTED = 7;
const BYPASSED_AT_DISPATCH = 9;

// Already accounted for, so there is nothing left for a dispatcher to do. A bypass belongs here
// for the same reason ManuallyResolved does: confirmation was never needed, either a dispatcher
// already spoke to the driver, or the driver is a standing DDC Shuttle exemption.
const isResolved = (status: number) =>
  status === CONFIRMED_IN_APP || status === CONFIRMED_BY_CALL || status === MANUALLY_RESOLVED
  || status === BYPASSED_AT_DISPATCH;

const statusText = (status: number) => {
  switch (status) {
    case 0: return 'Unconfirmed';
    case 1: return 'Confirmed in app';
    case 2: return 'Confirmed on call';
    case 3: return 'Calling driver';
    case 4: return 'Not confirmed';
    case 5: return 'Declined by driver';
    case 6: return 'Resolved by dispatch';
    case 7: return 'Driver requested a callback';
    case 8: return 'Driver not reached, all attempts used';
    case 9: return 'Confirmation not required, bypassed at dispatch';
    default: return 'Unknown';
  }
};

const DispatchConfirmationDialog: React.FC<Props> = ({ OrderID, CloseDialog }) => {
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState<DispatchConfirmation | null>(null);

  const refresh = React.useCallback(() => {
    fetchApi(`/api/Track/DispatchConfirmation/${OrderID}`)
      .then((response: DispatchConfirmation) => setConfirmation(response))
      .catch(() => alert('Unable to load the dispatch confirmation.'))
      .finally(() => setLoading(false));
  }, [OrderID]);

  useEffect(() => { refresh(); }, [refresh]);

  // Kendo keeps a themed button solid even when disabled, which reads as clickable.
  const themeWhenEnabled = (disabled: boolean) => disabled ? undefined : 'primary' as const;

  const post = (url: string, failureMessage: string) => {
    setLoading(true);
    fetchApi(url, {}, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          CloseDialog();
        } else {
          alert(response.ErrorMessage);
          setLoading(false);
        }
      })
      .catch(() => {
        alert(failureMessage);
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <React.Fragment>
        <div className="k-overlay" style={{ zIndex: 10002, opacity: 0.5 }} />
        <LoadingPanel />
      </React.Fragment>
    );
  }

  return (
    <Dialog title="Dispatch Confirmation" onClose={CloseDialog} className="dialog-w525">
      <div className="p-2">
        {confirmation?.Exists ? <React.Fragment>
          {/* Reminds dispatch which row they clicked, since Track can be busy and this dialog
              carries no other visible link back to a specific vehicle or driver. */}
          {(confirmation.VehicleNumber || confirmation.DriverNumber) && <div className="text-muted" style={{ fontSize: 12 }}>
            {[confirmation.VehicleNumber, confirmation.DriverNumber].filter(Boolean).join(' · ')}
          </div>}
          <div>
            <strong>{statusText(confirmation.Status)}</strong>
            {confirmation.CallAttemptCount > 0 && <span> &nbsp;&middot;&nbsp; {confirmation.CallAttemptCount} call attempt(s)</span>}
            {/* Always available: a fast way to dial the driver, e.g. to start a Zoom call, without leaving Track. */}
            {confirmation.DriverPhone && <span>
              &nbsp;&middot;&nbsp;
              <a href={`tel:${confirmation.DriverPhone}`} style={{ color: '#007bff' }}>{formatPhone(confirmation.DriverPhone)}</a>
            </span>}
          </div>
          <hr />
          {confirmation.Timeline.length === 0
            ? <div>Nothing recorded yet.</div>
            // Only the history scrolls, so the status line above it stays put on a long timeline.
            : <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                {/* Newest first: a dispatcher opening this wants the latest outcome, not the history. */}
                {confirmation.Timeline.slice().reverse().map((entry, index) => <div key={index} style={{ fontSize: 12 }}>
                  <strong>{entry.EventDescription}</strong> &nbsp;<span className="text-muted">{entry.TimeAgo}</span>
                  {entry.Detail && <div className="text-muted">{entry.Detail}</div>}
                </div>)}
              </div>}
        </React.Fragment> : <div>No dispatch confirmation exists for this order.</div>}
      </div>
      <DialogActionsBar>
        <Button onClick={CloseDialog}>Cancel</Button>
        {(() => {
          // Never offer to call a driver who is already accounted for, while a call is live, or
          // right after the agent already told them dispatch will call back: resending here would
          // just be a second call on top of that promise. Mark Confirmed stays open, since the
          // dispatcher's callback is exactly what closes this out.
          const resendDisabled = !confirmation?.Exists || isResolved(confirmation.Status)
            || confirmation.Status === CALL_IN_PROGRESS || confirmation.Status === CALLBACK_REQUESTED;
          const confirmDisabled = !confirmation?.Exists || isResolved(confirmation.Status);
          return <React.Fragment>
            <Button
              themeColor={themeWhenEnabled(resendDisabled)}
              disabled={resendDisabled}
              onClick={() => post(`/api/Track/ResendConfirmationCall/${OrderID}`, 'Unable to place the confirmation call.')}
            >
              Resend Call
            </Button>
            <Button
              themeColor={themeWhenEnabled(confirmDisabled)}
              disabled={confirmDisabled}
              onClick={() => post(`/api/Track/ConfirmDispatch/${OrderID}`, 'Unable to confirm the dispatch.')}
            >
              Mark Confirmed
            </Button>
          </React.Fragment>;
        })()}
      </DialogActionsBar>
    </Dialog>
  );
};

export default DispatchConfirmationDialog;
